/**
 * 運営提供SEカタログ (audio_catalog_items) の利用者向けAPI。
 *
 * docs/features/reference-audio-roles-plan.md フェーズ5b。§2.7 の一覧API
 * (`GET /api/organization/audio-catalog`) を呼ぶ。
 */
import { apiClient } from '../../services/apiClient';
import type { AudioReferenceUsage } from '../../components/generation/state/types';

export interface AudioCatalogItem {
  id: string;
  name: string;
  description: string | null;
  default_audio_role: AudioReferenceUsage | null;
  content_type: string | null;
  duration_seconds: number | null;
  file_size_bytes: number | null;
  sort_order: number;
  /** 提供元の名前 (docs/features/audio-catalog-admin-ui-plan.md §12)。未設定なら null。
   *  URL / ライセンスのメモは管理者向けの情報なので利用者向けレスポンスには含まれない。 */
  source_name: string | null;
  /** 試聴・添付用の署名付きURL。一覧APIが都度署名する (§2.7)。署名に失敗した項目のみ
   *  null (その項目は試聴・添付を無効化する)。 */
  preview_url: string | null;
  /** 2 層の区分: 'common' = 運営提供 (全パートナー共通) / 'partner' = 自パートナー独自
   *  (docs/features/free-bgm-catalog-plan.md)。partner_id そのものは返らない。 */
  scope: 'common' | 'partner';
}

interface ListResponse {
  items?: AudioCatalogItem[];
  error?: string;
}

/** 認証ユーザーの組織から可視な運営提供カタログ素材一覧 (is_active=true のみ・sort_order順)。 */
export async function fetchAudioCatalog(): Promise<AudioCatalogItem[]> {
  const { data } = await apiClient.get<ListResponse>('/organization/audio-catalog');
  return data.items ?? [];
}

/** カタログ素材を個人フォルダの素材へ複製した結果 (動画エディタの SE トラック用)。 */
export interface CopiedCatalogMaterial {
  material_id: string;
  is_temporary: boolean;
  name: string | null;
  duration_seconds: number | null;
  /** true = 同じカタログ項目から前に作った素材を使い回した (複製は 1 組織 1 回で冪等)。 */
  reused: boolean;
}

/**
 * 運営提供カタログの 1 件を、個人フォルダの素材 (temporary_materials) へ複製する
 * (docs/features/video-editor-capability-gaps-plan.md §4.3-D)。
 *
 * 動画エディタの SE トラックは素材参照 (`materialId`) しか持てないため、カタログの音を
 * 使うときは必ずこれを通して materialId を得る。同じカタログ項目を 2 回選んでも素材は増えない。
 */
export async function copyAudioCatalogItemToMaterials(itemId: string): Promise<CopiedCatalogMaterial> {
  const { data } = await apiClient.post<CopiedCatalogMaterial>(
    `/organization/audio-catalog/${itemId}/copy-to-materials`,
  );
  return data;
}
